/**
 * First-party analytics — tiny, dependency-free, and fail-silent by design.
 *
 * Mirrors `tapHistory.ts`: every path is wrapped so a storage/network hiccup can
 * never surface to the user or spam the console. Events are buffered in memory
 * and flushed in batches to the Fly server's `POST /api/events` (same-origin →
 * no CSP change, and `sendBeacon` works on unload). The server is the ONLY
 * writer of `app_events` — the browser holds no insert grant, so this can't be
 * abused beyond the server's per-IP rate limit.
 *
 * Privacy: `anon_id` is attached ONLY when the visitor accepted the consent
 * notice (`getAnonId()` returns null otherwise); `user_id` only when signed in.
 * No PII, no free text; the server never records an IP. `user_id` is a soft
 * aggregate signal — the metric that must be trustworthy (guest→account
 * conversion) is derived server-side from `anon_links`, not from these events.
 */
import { getAnonId } from "./anonId";
import { useAuthStore } from "@/services/authStore";

export type EventKind = "page_view" | "venue_view" | "directions_tap" | "share";
export type EventSurface =
  | "feed"
  | "nearby"
  | "map"
  | "offers"
  | "search"
  | "detail"
  | "recommended"
  | "quick";

interface QueuedEvent {
  kind: EventKind;
  anon_id: string | null;
  user_id: string | null;
  venue_id?: string;
  city?: string;
  surface?: EventSurface;
}

const ENDPOINT = "/api/events";
const FLUSH_INTERVAL_MS = 15_000;
const MAX_BUFFER = 25; // flush eagerly once the buffer reaches this

let buffer: QueuedEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let listenersBound = false;

function currentUserId(): string | null {
  try {
    return useAuthStore.getState().session?.user?.id ?? null;
  } catch {
    return null;
  }
}

// The `/showcase-cluj` demo runs on simulated data; its views must never land
// in the real traffic metrics. Toggled by the demo route on mount/unmount.
let suppressed = false;

export function setAnalyticsSuppressed(on: boolean): void {
  suppressed = on;
}

/**
 * Record one event. Never throws. `surface` is passed by callers that know
 * where the interaction happened (feed vs map vs offers …).
 */
export function track(
  kind: EventKind,
  opts: { venue_id?: string; city?: string; surface?: EventSurface } = {},
): void {
  if (suppressed) return;
  try {
    buffer.push({
      kind,
      anon_id: getAnonId(),
      user_id: currentUserId(),
      venue_id: opts.venue_id,
      city: opts.city,
      surface: opts.surface,
    });
    if (buffer.length >= MAX_BUFFER) flush();
    else ensureTimer();
  } catch {
    /* analytics must never throw into the app */
  }
}

function ensureTimer(): void {
  if (typeof window === "undefined") return;
  if (timer === null) timer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
  if (!listenersBound) {
    // Flush the tail on tab-hide / unload — sendBeacon survives the navigation.
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush(true);
    });
    window.addEventListener("pagehide", () => flush(true));
    listenersBound = true;
  }
}

/**
 * Send the buffered events. `useBeacon` uses `navigator.sendBeacon` (the only
 * transport guaranteed to complete during page unload); otherwise a normal
 * keepalive fetch. Both are best-effort and swallow all errors.
 */
export function flush(useBeacon = false): void {
  if (buffer.length === 0) return;
  const events = buffer;
  buffer = [];
  const payload = JSON.stringify({ events });
  try {
    if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      if (ok) return;
      // Beacon queue full → fall through to a best-effort fetch.
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    /* drop — analytics is best-effort */
  }
}
